/**
 * localStorage-backed persistence for AppData with versioned migrations.
 *
 * Versioning model:
 *   - Every persisted blob carries `version: DATA_VERSION` at write time.
 *   - On read we compare the stored version to DATA_VERSION. If it's
 *     older we run each pending migration in sequence (v1 → v2,
 *     v2 → v3, …) until the blob is current. Migrations only ADD
 *     fields with sensible defaults — they never delete or rewrite
 *     user data.
 *   - After migrations, `reconcile()` walks every top-level key the
 *     current AppData interface expects and fills in defaults for
 *     anything still missing or wrong-typed. Belt-and-suspenders
 *     against migrations that forgot a field.
 *   - Migrated/reconciled blobs are written back so the next cold
 *     start is a fast path with no migration work.
 *
 * Goal: a user who installed at v1 and now opens v6 never has to
 * "reset all data" to use new features.
 *
 * Adding a new version: append one entry to `migrations` (its index
 * means "migrate from version index+1 to version index+2") and bump
 * DATA_VERSION in types.ts. Existing entries must stay untouched and
 * in order — older clients that haven't migrated yet rely on them.
 */

import { processScheduledBills } from '../lib/scheduledOccurrences';
import { buildDefaultAppData } from './defaults';
import type { AppData } from './types';
import { DATA_VERSION } from './types';

const STORAGE_KEY = 'margin:appdata:v1';

type AnyRecord = Record<string, unknown>;

const isObject = (v: unknown): v is AnyRecord =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Each entry transforms the persisted blob from version (i + 1) to
 * version (i + 2). Append-only; never reorder or delete.
 *
 * Rules:
 *   - Only add missing fields with sensible defaults.
 *   - Never delete user data (transactions, categories, scheduled items,
 *     goals — even if the schema technically deprecated something).
 *   - Don't depend on the current AppData type — types may have moved
 *     on since this migration was written. Treat input as raw records.
 */
const migrations: Array<(data: AnyRecord) => AnyRecord> = [
  // v1 → v2: backfill `installedAt` (ISO timestamp) and `toolsUsed`
  // (string[]) on the root. These were added when the achievement
  // tracker shipped; v1 users opened the app and silently lost
  // achievement progress because the fields didn't exist. Best-effort
  // first-install stamp uses now() as a lower bound — we don't know the
  // real install time for a v1 user.
  (data) => {
    if (typeof data.installedAt !== 'string') {
      data.installedAt = new Date().toISOString();
    }
    if (!Array.isArray(data.toolsUsed)) {
      data.toolsUsed = [];
    }
    return data;
  },
  // v2 → v3: profile.monthlyIncome is no longer the budget anchor. All
  // income now lives on the Plan tab as either one-off transactions or
  // recurring scheduled income. To avoid breaking existing users, any
  // positive monthlyIncome becomes a recurring monthly "Salary" item
  // and the field is cleared. Their Today screen budget keeps working
  // with no manual step.
  (data) => {
    const profile = isObject(data.profile) ? { ...data.profile } : {};
    const amount =
      typeof profile.monthlyIncome === 'number' ? profile.monthlyIncome : 0;
    if (amount > 0) {
      const items = Array.isArray(data.scheduledItems)
        ? [...(data.scheduledItems as AnyRecord[])]
        : [];
      // Pick any income category as the categoryId — store auto-creates
      // a default income category at install. Fall back to '' if the
      // user wiped categories somehow; the row still renders.
      const incomeCategoryId = Array.isArray(data.categories)
        ? ((data.categories as Array<{ id?: unknown; type?: unknown }>)
            .find((c) => c?.type === 'income')?.id as string | undefined) ?? ''
        : '';
      const todayIso = new Date().toISOString().slice(0, 10);
      items.push({
        id: `migrated-salary-${Date.now()}`,
        name: 'Salary',
        amount,
        type: 'income',
        categoryId: incomeCategoryId,
        frequency: 'monthly',
        nextDate: todayIso,
        active: true,
      });
      data.scheduledItems = items;
    }
    delete profile.monthlyIncome;
    data.profile = profile;
    return data;
  },
  // v3 → v4: add hero-view toggle + rollover policy.
  //   - heroView (daily | period): smart default from income shape so
  //     existing users see the right number first. Spread or recurring
  //     income → 'daily'. Lump-sum-only → 'period'. No income yet →
  //     'daily'. Once the user taps the toggle the explicit value sticks.
  //   - rollover (continuous | fresh): default 'continuous' so prior
  //     surplus/deficit folds forward into the current period's pool —
  //     savings momentum across paydays, which is what most users expect.
  (data) => {
    const profile = isObject(data.profile) ? { ...data.profile } : {};
    if (profile.heroView !== 'daily' && profile.heroView !== 'period') {
      const txs = Array.isArray(data.transactions)
        ? (data.transactions as Array<{
            type?: unknown;
            spreadDays?: unknown;
          }>)
        : [];
      const items = Array.isArray(data.scheduledItems)
        ? (data.scheduledItems as Array<{
            type?: unknown;
            active?: unknown;
          }>)
        : [];
      const incomeTxs = txs.filter((t) => t?.type === 'income');
      const hasSpreadIncome = incomeTxs.some(
        (t) => typeof t?.spreadDays === 'number' && t.spreadDays > 1,
      );
      const hasRecurringIncome = items.some(
        (i) => i?.type === 'income' && i?.active === true,
      );
      const hasLumpOnly =
        incomeTxs.length > 0 && !hasSpreadIncome && !hasRecurringIncome;
      profile.heroView = hasLumpOnly ? 'period' : 'daily';
    }
    if (profile.rollover !== 'continuous' && profile.rollover !== 'fresh') {
      profile.rollover = 'continuous';
    }
    data.profile = profile;
    return data;
  },
  // v4 → v5: workDaysPerWeek for the PYF model. Default 5 (Mon–Fri),
  // matching the original hard-coded behavior so existing PYF users see
  // identical numbers post-upgrade. Users on 4×10s, 3×12s, etc. can
  // change it in Settings.
  (data) => {
    const profile = isObject(data.profile) ? { ...data.profile } : {};
    const wdpw = profile.workDaysPerWeek;
    if (typeof wdpw !== 'number' || wdpw < 1 || wdpw > 7) {
      profile.workDaysPerWeek = 5;
    }
    data.profile = profile;
    return data;
  },
  // v5 → v6: manual `accounts` array for the cash-position visibility
  // layer. Default to empty so existing users see no UI change until
  // they add an account themselves.
  (data) => {
    if (!Array.isArray(data.accounts)) {
      data.accounts = [];
    }
    return data;
  },
  // v6 → v7: profile.quickPresets — Today-screen one-tap shortcuts.
  // Default to empty so the chip row stays hidden until the user
  // creates one.
  (data) => {
    const profile = isObject(data.profile) ? { ...data.profile } : {};
    if (!Array.isArray(profile.quickPresets)) {
      profile.quickPresets = [];
    }
    data.profile = profile;
    return data;
  },
  // v7 → v8: profile.netWorthHistory — rolling daily snapshots of
  // total net worth + per-category breakdown. Default empty; the
  // store appends today's entry on first cold-start.
  (data) => {
    const profile = isObject(data.profile) ? { ...data.profile } : {};
    if (!Array.isArray(profile.netWorthHistory)) {
      profile.netWorthHistory = [];
    }
    data.profile = profile;
    return data;
  },
  // v8 → v9: profile.achievements — full state for the achievement
  // subsystem. Existing users get installedAt backdated by 7 days so
  // the cold-start cooldown doesn't punish people who've been around.
  (data) => {
    const profile = isObject(data.profile) ? { ...data.profile } : {};
    if (!isObject(profile.achievements)) {
      const sevenDaysAgo = new Date(
        Date.now() - 7 * 24 * 60 * 60 * 1000,
      ).toISOString();
      profile.achievements = {
        unlocks: [],
        progress: [],
        streaks: {
          daysLogged: { current: 0, best: 0, lastDate: null },
          daysUnderPace: { current: 0, best: 0, lastDate: null },
          noSpendDays: { current: 0, best: 0, lastDate: null },
          pyfStreak: { current: 0, best: 0, lastDate: null },
        },
        installedAt: sevenDaysAgo,
        achievementsEnabled: true,
      };
    }
    data.profile = profile;
    return data;
  },
];

/**
 * Apply pending migrations to the stored blob. Returns the migrated
 * data plus a `changed` flag so the caller can persist it back.
 */
function runMigrations(input: AnyRecord): {
  data: AnyRecord;
  changed: boolean;
} {
  // Treat anything missing/non-numeric as v1 — the original release
  // didn't always stamp the version, and v1 is the earliest shape we
  // have migrations defined for.
  let v =
    typeof input.version === 'number' && input.version >= 1
      ? input.version
      : 1;
  const startV = v;
  let current: AnyRecord = { ...input };
  while (v < DATA_VERSION) {
    const step = migrations[v - 1]; // migrations[0] = v1 → v2
    if (!step) break;
    current = step(current);
    v += 1;
    current.version = v;
  }
  return { data: current, changed: v !== startV };
}

/**
 * Safety net. After migrations, walk every top-level field on AppData
 * and fill in defaults for anything missing or wrong-typed. Nested
 * optional fields (e.g. Profile.hourlyRate, Transaction.spreadDays)
 * stay as-is — they're allowed to be undefined and the consumers
 * already handle that. The Profile object gets a defaults-spread so
 * required fields like `payCycle` always exist.
 */
function reconcile(stored: AnyRecord): AppData {
  const defaults = buildDefaultAppData();
  return {
    version: DATA_VERSION,
    profile: isObject(stored.profile)
      ? { ...defaults.profile, ...stored.profile }
      : defaults.profile,
    categories: Array.isArray(stored.categories)
      ? (stored.categories as AppData['categories'])
      : defaults.categories,
    transactions: Array.isArray(stored.transactions)
      ? (stored.transactions as AppData['transactions'])
      : defaults.transactions,
    scheduledItems: Array.isArray(stored.scheduledItems)
      ? (stored.scheduledItems as AppData['scheduledItems'])
      : defaults.scheduledItems,
    goals: Array.isArray(stored.goals)
      ? (stored.goals as AppData['goals'])
      : defaults.goals,
    achievements: Array.isArray(stored.achievements)
      ? (stored.achievements as AppData['achievements'])
      : defaults.achievements,
    installedAt:
      typeof stored.installedAt === 'string'
        ? stored.installedAt
        : defaults.installedAt,
    toolsUsed: Array.isArray(stored.toolsUsed)
      ? (stored.toolsUsed as string[])
      : defaults.toolsUsed,
    theme:
      stored.theme === 'light' ||
      stored.theme === 'dark' ||
      stored.theme === 'system'
        ? stored.theme
        : defaults.theme,
    accentColor:
      typeof stored.accentColor === 'string'
        ? stored.accentColor
        : defaults.accentColor,
    rated: typeof stored.rated === 'boolean' ? stored.rated : defaults.rated,
    rateSnoozed:
      typeof stored.rateSnoozed === 'string' ? stored.rateSnoozed : undefined,
    rateNever:
      typeof stored.rateNever === 'boolean' ? stored.rateNever : undefined,
    proTrial:
      isObject(stored.proTrial) && typeof stored.proTrial.started === 'string'
        ? { started: stored.proTrial.started }
        : undefined,
    proPurchased:
      typeof stored.proPurchased === 'boolean'
        ? stored.proPurchased
        : undefined,
    accounts: Array.isArray(stored.accounts)
      ? (stored.accounts as AppData['accounts'])
      : defaults.accounts,
  };
}

const hasStorage = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const k = '__margin_probe__';
    window.localStorage.setItem(k, k);
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
};

export function loadAppData(): AppData {
  if (!hasStorage()) return buildDefaultAppData();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildDefaultAppData();
    const parsed = JSON.parse(raw);
    if (!isObject(parsed)) return buildDefaultAppData();
    const { data: migrated, changed } = runMigrations(parsed);
    const reconciled = reconcile(migrated);
    // Materialize any due scheduled bills into real transactions so the
    // Activity tab and running balance pick them up the moment the app
    // opens — covers "app was closed for 3 days, rent came due."
    const processed = processScheduledBills(reconciled);
    // Persist immediately if anything actually changed (migration ran,
    // reconcile filled gaps, or scheduled bills got materialized).
    // Without this the user pays the migration cost on every cold start
    // and bills would be regenerated only at runtime, not persisted.
    const reconcileFilledGaps = reconciled.version !== parsed.version;
    const billsProcessed = processed !== reconciled;
    if (changed || reconcileFilledGaps || billsProcessed) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(processed));
      } catch {
        /* quota or storage disabled — runtime data is still correct */
      }
    }
    return processed;
  } catch {
    return buildDefaultAppData();
  }
}

export function saveAppData(data: AppData): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Quota exceeded or storage disabled — nothing useful to do here.
    // The Pro tier syncs to Supabase as a backstop.
  }
}

/** Wipe all persisted data. Used by Settings > Reset All Data. */
export function clearAppData(): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/** Exposed for tests + Settings > Export. */
export const STORAGE_KEY_NAME = STORAGE_KEY;
